// "Axxion Export" - ported from reporting-hub/frontend/src/components/
// preflight/report/AxxionExportDialog.tsx. A differently-shaped variant of
// the Cashflow Projection Excel export (see compartment_report_calc_service's
// cashflow_projection_export.py `build_axxion_export_workbook()`): a
// "LoanTape" sheet is added, and the "CashFlows" sheet drops
// `investment_key`, gains a `payment_date_excel` first column, and only
// keeps rows with `planned_payment_date` after the chosen cutoff date and no
// `actual_payment_date` set yet. Unlike "Static"/"With Formulas" (a direct
// download from the caller), this variant needs that cutoff date first -
// hence its own dialog instead of a plain menu item, same as the original.
//
// The actual export request (variant=axxion&cutoff_date=...) is issued by
// the caller (CashflowProjectionShell) via the `onExport` callback, so this
// component owns no axios call of its own - it only owns the date picker's
// local UI state.

import React, { useState } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { Calendar } from 'primereact/calendar';
import { isoToDate, dateToIso } from '../calculation/calcFormat';

const AxxionExportDialog = ({ reportingId, visible, onHide, defaultCutoffDate, onExport }) => {
  const [cutoffDate, setCutoffDate] = useState(defaultCutoffDate || '');
  const [exporting, setExporting] = useState(false);

  // Re-seed the draft from the shell's default every time the dialog opens -
  // mirrors the original's useState(defaultCutoffDate) initial value, which
  // only ever ran once per mount there because that dialog was a fresh
  // component instance each open; here the dialog is a stable instance kept
  // mounted by the caller, so this needs an explicit re-seed on `visible`.
  const handleShow = () => setCutoffDate(defaultCutoffDate || '');

  const handleClose = () => {
    if (exporting) return;
    onHide();
  };

  const handleExportClick = async () => {
    if (!cutoffDate) return;
    setExporting(true);
    try {
      await onExport(cutoffDate);
      onHide();
    } finally {
      setExporting(false);
    }
  };

  return (
    <Dialog header="Axxion Export" visible={visible} onShow={handleShow} onHide={handleClose} closable={!exporting} style={{ width: '28rem' }} maximizable>
      <p className="text-sm text-color-secondary">
        The CashFlows sheet excludes rows with a Planned Payment Date on or before the cutoff date below, as well as
        any row that already has an Actual Payment Date.
      </p>
      <div className="flex flex-column gap-2 mb-4">
        <label htmlFor="axxion-cutoff-date" className="text-sm text-color-secondary">
          Cutoff date
        </label>
        <Calendar
          id="axxion-cutoff-date"
          value={isoToDate(cutoffDate)}
          onChange={(e) => setCutoffDate(dateToIso(e.value))}
          dateFormat="yy-mm-dd"
          showIcon
        />
      </div>
      <div className="flex justify-content-end gap-2">
        <Button label="Cancel" className="p-button-outlined" onClick={handleClose} disabled={exporting} />
        <Button
          label={exporting ? 'Exporting…' : 'Export'}
          icon={exporting ? 'pi pi-spin pi-spinner' : 'pi pi-download'}
          onClick={handleExportClick}
          disabled={!cutoffDate || exporting}
        />
      </div>
    </Dialog>
  );
};

export default AxxionExportDialog;
